import { isNonEmpty } from 'fp-ts/Array';
import { startsWith, toLower } from 'lodash';
import {
    AbortError,
    AdditionalAuthorizationError,
    BadRequestError,
    BusinessError,
    deserializeErrorCode,
    NetworkError,
    NotAllowedError,
    NotAuthorizedError,
    NotFoundError,
    QuotaExceededError,
    RequestEntityTooLarge,
    ResponseError,
    TimeoutError,
} from '@mews-framework/errors';
import { HttpStatusCode, HttpStatusCodeGroup } from '@mews/enums';
import { getHttpStatusCodeGroup } from './httpStatusCodeGroup';

type ErrorType = new (
    message: string
) =>
    | TimeoutError
    | BusinessError
    | BadRequestError
    | NotFoundError
    | RequestEntityTooLarge
    | QuotaExceededError
    | NotAllowedError
    | Error;

export class RequestError extends Error {
    public data?: unknown;
    public errorCode?: string;
    public requestUrl?: string;
    public statusCode?: number;
}

export interface SendRequestOptions {
    accept?: string;
    credentials?: RequestCredentials;
    method?: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'DELETE' | 'CONNECT' | 'OPTIONS' | 'TRACE' | 'PATCH';
    contentType?: string;
    autoContentType?: boolean;
    signal?: AbortSignal;
    shouldReturnResponseData?: boolean;
    customHeaders?: Record<string, string>;
    keepAlive?: boolean;
    stringifyData?: boolean;
}

export default async function sendRequest(
    url: string,
    {
        accept = 'application/json',
        credentials = 'omit',
        method = 'POST',
        contentType = 'application/json',
        customHeaders = {},

        // Sending FormData requires undefined Content-Type header
        autoContentType = false,

        // AbortController signal
        signal,

        // Includes response data in return value - used only by POST form submission from C# templates
        shouldReturnResponseData = false,

        keepAlive = false,
        stringifyData = true,
    }: SendRequestOptions = {},
    data: BodyInit | object | null = null
) {
    const init: RequestInit = {
        credentials,
        method,
        headers: {
            ...customHeaders,
            Accept: accept,
        },
        signal,
        keepalive: keepAlive,
    };

    if (!autoContentType) {
        (init.headers as Record<string, string>)['Content-Type'] = contentType;
    }

    if (
        data instanceof global.FormData ||
        (!stringifyData &&
            (data instanceof global.FormData ||
                data instanceof Blob ||
                data instanceof Buffer ||
                data instanceof ArrayBuffer ||
                data instanceof Uint8Array ||
                data instanceof URLSearchParams ||
                data instanceof ReadableStream ||
                typeof data === 'string'))
    ) {
        init.body = data as BodyInit;
    } else if (data) {
        init.body = JSON.stringify(data);
    }

    return window
        .fetch(url, init)
        .catch((error) => {
            // fetch throws TypeError on network errors
            if (error instanceof TypeError) {
                throw new NetworkError(error.message);
            }

            // AbortErrors are expected with AbortController.signal present
            if (signal && error instanceof window.DOMException && error.name === 'AbortError') {
                throw new AbortError(error.message);
            }

            throw error;
        })
        .then(async (response) => checkStatus(response, { accept, requestUrl: url, shouldReturnResponseData }));
}

interface CheckStatusOptions {
    accept: string;
    requestUrl: string;
    shouldReturnResponseData: boolean;
}

const handleGraphQlErrors = async (response: Response, requestUrl: string) => {
    const result = await response.json();

    // By default graphql endpoints sends errors as an array object
    const errorData = result?.errors;
    if (errorData && isNonEmpty(errorData)) {
        errorData.forEach((error: unknown) => {
            throw createError(BusinessError, response, requestUrl, error);
        });
    }

    throw createError(BusinessError, response, requestUrl);
};

async function checkStatus(response: Response, { accept, requestUrl, shouldReturnResponseData }: CheckStatusOptions) {
    const statusGroup = getHttpStatusCodeGroup(response.status);
    const contentType = toLower(response.headers.get('content-type') || '');

    if (statusGroup === HttpStatusCodeGroup.ServerErrors) {
        if (response.status === HttpStatusCode.Timeout.value) {
            throw createError(TimeoutError, response, requestUrl);
        }

        // Handle graphql errors
        if (startsWith(contentType, 'application/graphql-response+json')) {
            return handleGraphQlErrors(response, requestUrl);
        }

        throw createError(BusinessError, response, requestUrl);
    }

    if (response.status === HttpStatusCode.Locked.value) {
        throw createError(AdditionalAuthorizationError, response, requestUrl);
    }

    if (response.status === HttpStatusCode.NotFound.value) {
        throw createError(NotFoundError, response, requestUrl);
    }

    if (response.status === HttpStatusCode.RequestEntityTooLarge.value) {
        throw createError(RequestEntityTooLarge, response, requestUrl);
    }

    if (response.status === HttpStatusCode.TooManyRequests.value) {
        throw createError(QuotaExceededError, response, requestUrl);
    }

    if (response.ok && accept !== '*/*' && accept !== 'application/octet-stream' && !startsWith(contentType, accept)) {
        return response.text().then((text) => {
            throw new ResponseError(
                `Unexpected response: "${text}", Content-Type: ${contentType}, Expected Content-Type: ${accept}, requestUrl: ${requestUrl}`
            );
        });
    }

    const getContent = async () => {
        if (startsWith(contentType, 'application/json')) return response.json();
        if (startsWith(contentType, 'application/graphql-response+json')) return response.json();
        if (startsWith(contentType, 'application/pdf')) return response.blob();
        if (accept === 'application/octet-stream') return response.blob();
        return response.text();
    };

    return getContent().then((data) => {
        if (statusGroup === HttpStatusCodeGroup.ClientErrors) {
            if (response.status === HttpStatusCode.NotAuthorized.value) {
                throw createError(NotAuthorizedError, response, requestUrl, data);
            }
            if (response.status === HttpStatusCode.Forbidden.value) {
                throw createError(NotAllowedError, response, requestUrl, data);
            }

            if (response.status === HttpStatusCode.BadRequest.value) {
                throw createError(BadRequestError, response, requestUrl, data);
            }

            // Handle graphql errors
            if (startsWith(contentType, 'application/graphql-response+json')) {
                return handleGraphQlErrors(response, requestUrl);
            }

            throw createError(BusinessError, response, requestUrl, data);
        }

        if (response.ok) {
            if (shouldReturnResponseData) {
                return {
                    data,
                    headers: response.headers,
                    redirected: response.redirected,
                };
            }

            return data;
        }

        throw createError(Error, response, requestUrl, data);
    });
}

function createError(ErrorType: ErrorType, response: Response, requestUrl: string, data?: unknown) {
    const { status, statusText } = response;
    const message =
        (data as $TSFixMe)?.Message || (data as $TSFixMe)?.message || statusText || 'Unexpected error occurred';

    const error = new ErrorType(message) as RequestError;

    error.data = data;

    const errorCode = deserializeErrorCode(data);
    if (errorCode != null) {
        error.errorCode = errorCode;
    }

    error.requestUrl = requestUrl;
    error.statusCode = status;

    return error;
}
